import React, { useState } from 'react';
import { Container, Grid, Paper, Typography, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Sidebar from '../Sidebar/Sidebar';
import useAuth from '../../hooks/useAuth';
import DicomUploadPanel from './DicomUploadPanel';
import DicomPreviewPanel from './DicomPreviewPanel';
import DicomOptionsDialog from './DicomOptionsDialog'; // <--- NEW IMPORT

import { pseudoAnonymizeDicomPII } from '../../services/dicomService';
import { createBatchId } from '../../shared/createBatchId';

const PseudoDicomAnonymizerPage = () => {
  const { isLoggedIn, user, isLoading } = useAuth();
  const { t } = useTranslation();

  const [fileItems, setFileItems] = useState([]);
  const [activeFileId, setActiveFileId] = useState(null);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  
  // --- UPDATED OPTIONS STATE FOR DICOM ---
  const [options, setOptions] = useState({
    save_to_db: false,
    profileId: null,
    // Start with the most critical tags pre-selected for convenience
    tags_to_pseudonymize: ['PatientName', 'PatientID', 'PatientBirthDate', 'SOPInstanceUID', 'StudyInstanceUID', 'SeriesInstanceUID'], 
  });

  const activeFile = fileItems.find(f => f.id === activeFileId) || null;
  const hasPendingFiles = fileItems.some(f => f.status === 'ready');

  const handleConfirmOptions = async () => {
    setOptionsOpen(false);
    if (!hasPendingFiles || isProcessing) return;

    setIsProcessing(true);
    const groupId = createBatchId();

    const pendingItems = fileItems.filter(f => f.status === 'ready' || f.status === 'error');
    
    for (const item of pendingItems) {
      setFileItems(prev => prev.map(f => 
        f.id === item.id ? { ...f, status: 'uploading', progress: 30, error: '' } : f
      ));

      const res = await pseudoAnonymizeDicomPII(item.file, {
        ...options,
        anonymizationGroupId: groupId,
      });

      if (!res.success) {
        setFileItems(prev => prev.map(f => 
          f.id === item.id ? { ...f, status: 'error', progress: 100, error: res.error || t('common.error') } : f
        ));
      } else {
        const { blob, filename } = res.data || {};
        setFileItems(prev => prev.map(f => 
          f.id === item.id ? { 
            ...f, 
            status: 'success', 
            progress: 100, 
            blob, 
            downloadName: filename || `pseudonymized_${f.name}` 
          } : f
        ));
      }
    }
    
    setIsProcessing(false);
  };

  const renderSkeleton = () => (
    <>
      <Sidebar user={user} />
      <Container maxWidth="xl">
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 4 }, 
            mt: 4, 
            borderRadius: 3, 
            border: '1px solid', 
            borderColor: 'divider', 
            bgcolor: 'background.paper' 
          }}
        >
          <Skeleton variant="text" width={260} height={50} />
          <Skeleton variant="text" width={520} height={32} sx={{ mb: 3 }} />
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Skeleton variant="rectangular" height={600} sx={{ borderRadius: 2 }} />
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </>
  );

  if (!isLoggedIn || isLoading) return renderSkeleton();

  return (
    <>
      <Sidebar user={user} />
      <Container maxWidth="xl">
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 4 }, 
            mt: 4, 
            mb: 5, 
            borderRadius: 3, 
            border: '1px solid', 
            borderColor: 'divider', 
            bgcolor: 'background.paper' 
          }}
        >
          <Grid container spacing={4}>
            
            <Grid item xs={12}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                {t('PseudoDicomPage.title', 'Pseudonymizácia DICOM')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {t('PseudoDicomPage.subtitle', 'Nahrajte .dcm súbory, skontrolujte náhľad a identifikátory a spustite dávkovú pseudonymizáciu.')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <DicomUploadPanel 
                fileItems={fileItems}
                setFileItems={setFileItems}
                activeFileId={activeFileId}
                setActiveFileId={setActiveFileId}
                isProcessing={isProcessing}
                hasPendingFiles={hasPendingFiles}
                onOpenOptions={() => setOptionsOpen(true)}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <DicomPreviewPanel activeFile={activeFile} isProcessing={isProcessing} />
            </Grid>

          </Grid>
        </Paper>
      </Container>

      {/* NEW DICOM DIALOG */}
      <DicomOptionsDialog
        open={optionsOpen}
        options={options}
        onChangeOptions={setOptions}
        onClose={() => setOptionsOpen(false)}
        onConfirm={handleConfirmOptions}
        loading={isProcessing}
      />
    </>
  );
};

export default PseudoDicomAnonymizerPage;